// Per-metric "how is this number computed" content for the health-panel
// trend-point popup. Keyed by the BACKEND parameter name (the `parameter`
// field of /health-trend responses), not the frontend config key.
//
// The formulas mirror packages/fleet_tools/metrics.py — since #1269 the single
// backend copy (services/health_analysis_service.py imports from there) — keep
// this display copy in sync with it, including the underivability gate. Text is
// English-only, consistent with the English metric titles in the health catalogue.

import { TrendChartPoint } from '../types/health.types';

export interface SubstitutedExplanation {
  /** Generic formula, human-readable. */
  formula: string;
  /** One or two sentences on why the metric is computed this way. */
  reasoning: string;
  /**
   * The formula with this flight's values plugged in, one line per step.
   * Empty when the chart point doesn't carry the source values.
   */
  substitution: string[];
  /** True for plain flight-averages with no derivation. */
  direct: boolean;
}

type ChartPoint = TrendChartPoint;

function num(point: ChartPoint, key: keyof TrendChartPoint): number | null {
  const v = point[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

const fmt = (v: number, digits = 2) => v.toFixed(digits);

/** MAT engine-power correction (W): 37 W per °F above 90 °F manifold air. */
function matCorrection(matF: number): number {
  return matF < 90 ? 0 : (matF - 90) * 37;
}

function mslLines(eff: number, altM: number): string[] {
  const factor = 1 + 0.0001 * (750 - altM);
  return [
    `${fmt(eff)} % ÷ (1 + 0.0001 × (750 − ${fmt(altM, 1)} m))`,
    `= ${fmt(eff)} % ÷ ${fmt(factor, 4)} = ${fmt(eff / factor)} %`,
  ];
}

/** Mirrors MIN_MAT_CORRECTED_ENGINE_POWER_W in packages/fleet_tools/metrics.py. */
const MIN_MAT_CORRECTED_ENGINE_POWER_W = 0.1;

function matLines(
  pmuW: number,
  matC: number,
  engineW: number
): { lines: string[]; result: number | null } {
  const matF = (matC * 9) / 5 + 32;
  const corr = matCorrection(matF);
  const corrected = engineW - corr;
  // Same gate as the backend (#1269): a consumed denominator is not a number.
  // A point this applies to is never plotted, so this branch only renders if
  // the two copies ever disagree — in which case refusing is the honest side.
  if (!(corrected >= MIN_MAT_CORRECTED_ENGINE_POWER_W)) {
    return {
      lines: [
        `MAT ${fmt(matC, 1)} °C = ${fmt(matF, 1)} °F → derate ${fmt(corr, 0)} W`,
        `derate ≥ engine power (${fmt(engineW, 0)} W) — efficiency not derivable`,
      ],
      result: null,
    };
  }
  const result = (pmuW * 100) / corrected;
  return {
    lines: [
      `MAT ${fmt(matC, 1)} °C = ${fmt(matF, 1)} °F → derate ${fmt(corr, 0)} W`,
      `(${fmt(pmuW, 0)} W × 100) ÷ (${fmt(engineW, 0)} W − ${fmt(corr, 0)} W) = ${fmt(result)} %`,
    ],
    result,
  };
}

const EXPLANATIONS: Record<string, (point: ChartPoint) => SubstitutedExplanation> = {
  avg_system_efficiency: () => ({
    direct: true,
    formula: 'mean(electrical power out ÷ engine power in) over the flight',
    reasoning:
      'Raw drivetrain efficiency: how much of the engine’s mechanical power reaches the ' +
      'electrical bus, averaged across the whole flight. Not corrected for altitude or ' +
      'intake temperature — compare like-for-like flights only.',
    substitution: [],
  }),

  normalized_system_eff_msl: (point) => {
    const eff = num(point, 'avg_system_efficiency');
    const alt = num(point, 'avg_gps_alt[m]');
    return {
      direct: false,
      formula: 'efficiency ÷ (1 + 0.0001 × (750 − altitude MSL))',
      reasoning:
        'Thinner air at altitude makes raw efficiency read higher. This rescales every ' +
        'flight to a common 750 m MSL reference (≈1 % per 100 m), so flights flown at ' +
        'different sites are comparable.',
      substitution: eff !== null && alt !== null ? mslLines(eff, alt) : [],
    };
  },

  normalized_system_eff_mat: (point) => {
    const pmu = num(point, 'avg_pmu_power');
    const mat = num(point, 'avg_mat');
    const engine = num(point, 'avg_engine_power');
    return {
      direct: false,
      formula:
        '(electrical power × 100) ÷ (engine power − MAT derate), derate = 37 W per °F above 90 °F',
      reasoning:
        'Hot manifold air derates the engine, which would show up as an efficiency loss. ' +
        'Subtracting the expected temperature derate isolates the drivetrain’s own ' +
        'condition from the weather.',
      substitution:
        pmu !== null && mat !== null && engine !== null ? matLines(pmu, mat, engine).lines : [],
    };
  },

  normalized_system_eff_combined: (point) => {
    const pmu = num(point, 'avg_pmu_power');
    const mat = num(point, 'avg_mat');
    const engine = num(point, 'avg_engine_power');
    const alt = num(point, 'avg_gps_alt[m]');
    let substitution: string[] = [];
    if (pmu !== null && mat !== null && engine !== null && alt !== null) {
      const matStep = matLines(pmu, mat, engine);
      // Underivable MAT step: show why and stop — there is no number to
      // altitude-correct.
      substitution =
        matStep.result === null
          ? matStep.lines
          : [...matStep.lines, ...mslLines(matStep.result, alt)];
    }
    return {
      direct: false,
      formula: 'MAT-normalized efficiency ÷ (1 + 0.0001 × (750 − altitude MSL))',
      reasoning:
        'Applies both corrections — intake-temperature derate and altitude rescaling to ' +
        '750 m MSL — giving the most site- and weather-independent view of drivetrain health.',
      substitution,
    };
  },

  avg_mosfet_temperature: () => ({
    direct: true,
    formula: 'mean(MOSFET temperature) over the flight',
    reasoning:
      'Flight-average temperature of the power-stage MOSFETs. A rising trend across ' +
      'flights points at degrading cooling or increasing electrical load.',
    substitution: [],
  }),

  cht_1_minus_oat: (point) => {
    const cht = num(point, 'avg_cht_1');
    const oat = num(point, 'avg_oat');
    return {
      direct: false,
      formula: 'CHT1 − OAT',
      reasoning:
        'Cylinder-head temperature relative to outside air. Subtracting ambient removes ' +
        'the weather, so the trend reflects engine cooling performance, not the season.',
      substitution:
        cht !== null && oat !== null
          ? [`${fmt(cht, 1)} °C − ${fmt(oat, 1)} °C = ${fmt(cht - oat)} °C`]
          : [],
    };
  },
};

/**
 * Explanation for one chart point, or null when the parameter is unknown
 * (the popup then falls back to date + value only).
 */
export function explainDataPoint(
  parameter: string | undefined,
  point: ChartPoint
): SubstitutedExplanation | null {
  if (!parameter) return null;
  const build = EXPLANATIONS[parameter];
  return build ? build(point) : null;
}
